// @ts-nocheck
/**
 * MediaGallery Component
 * Carbon Design System DataTable with thumbnail previews and filtering
 * Epic 75 - Work Item 3: R2 Object Storage and Content Delivery Architecture
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  TableToolbarMenu,
  TableToolbarAction,
  TableSelectAll,
  TableSelectRow,
  TableBatchActions,
  TableBatchAction,
  OverflowMenu,
  OverflowMenuItem,
  Pagination,
  Tag,
  Button,
  Modal,
  Tabs,
  Tab,
  TabList,
  TabPanels,
  TabPanel,
  Tile,
  SkeletonText,
  InlineNotification,
  ContentSwitcher,
  Switch,
} from '@carbon/react';
import {
  Download,
  Share,
  Delete,
  View,
  Edit,
  Filter,
  Document,
  Video,
  Image as ImageIcon,
  Grid,
  List,
  CloudUpload,
} from '@carbon/react/icons';

interface MediaFile {
  file_id: string;
  file_name: string;
  file_size: number;
  mime_type: string;
  category: string;
  file_type: string;
  visibility: string;
  cdn_url: string;
  thumbnail_url?: string;
  uploaded_by: string;
  tags?: string[];
  description?: string;
  created_at: string;
  updated_at: string;
  view_count: number;
  download_count: number;
}

interface MediaGalleryProps {
  userId?: string;
  category?: string;
  onFileSelect?: (file: MediaFile) => void;
  onFileDelete?: (fileId: string) => void;
  onFileShare?: (fileId: string) => void;
  enableBulkOperations?: boolean;
  viewMode?: 'grid' | 'list';
}

export const MediaGallery: React.FC<MediaGalleryProps> = ({
  userId,
  category: filterCategory,
  onFileSelect,
  onFileDelete,
  onFileShare,
  enableBulkOperations = true,
  viewMode: initialViewMode = 'list',
}) => {
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState(initialViewMode);
  const [activeCategory, setActiveCategory] = useState(filterCategory || 'all');
  const [searchTerm, setSearchTerm] = useState('');
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedFileForShare, setSelectedFileForShare] = useState<MediaFile | null>(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);

  // Gallery categories
  const [gallery, setGallery] = useState<{
    images: MediaFile[];
    videos: MediaFile[];
    documents: MediaFile[];
    portfolios: MediaFile[];
    all: MediaFile[];
  }>({
    images: [],
    videos: [],
    documents: [],
    portfolios: [],
    all: [],
  });

  const fetchGallery = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
        ...(activeCategory !== 'all' && { category: activeCategory }),
        ...(searchTerm && { search: searchTerm }),
      });

      const response = await fetch(`/api/v1/storage/gallery/${userId || ''}?${queryParams}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch gallery');
      }

      const data = await response.json();
      setGallery(data.gallery);
      setFiles(data.gallery.all);
      setTotalItems(data.pagination.total);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId, page, pageSize, activeCategory, searchTerm]);

  useEffect(() => {
    fetchGallery();
  }, [fetchGallery]);

  const handleFileDownload = async (file: MediaFile) => {
    try {
      const response = await fetch(`/api/v1/storage/download/${file.file_id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.file_name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  const handleBulkDelete = async () => {
    if (!onFileDelete || selectedFiles.length === 0) return;

    try {
      const response = await fetch('/api/v1/storage/files/bulk-delete', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ fileIds: selectedFiles }),
      });

      if (!response.ok) {
        throw new Error('Bulk delete failed');
      }

      setSelectedFiles([]);
      fetchGallery();
    } catch (error) {
      console.error('Bulk delete error:', error);
    }
  };

  const handleShare = (file: MediaFile) => {
    setSelectedFileForShare(file);
    setShareModalOpen(true);
    if (onFileShare) {
      onFileShare(file.file_id);
    }
  };

  const getCategoryIcon = (category: string) => {
    const iconProps = { size: 16 };
    switch (category) {
      case 'document':
        return <Document {...iconProps} />;
      case 'video':
        return <Video {...iconProps} />;
      case 'image':
        return <ImageIcon {...iconProps} />;
      default:
        return <CloudUpload {...iconProps} />;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const headers = [
    { key: 'preview', header: 'Preview' },
    { key: 'name', header: 'Name' },
    { key: 'category', header: 'Category' },
    { key: 'size', header: 'Size' },
    { key: 'visibility', header: 'Visibility' },
    { key: 'created', header: 'Created' },
    { key: 'stats', header: 'Stats' },
    { key: 'actions', header: 'Actions' },
  ];

  const rows = files.map(file => ({
    id: file.file_id,
    preview: file.thumbnail_url ? (
      <img
        src={file.thumbnail_url}
        alt={file.file_name}
        style={{
          width: '3rem',
          height: '3rem',
          objectFit: 'cover',
          borderRadius: 'var(--cds-border-radius)',
        }}
      />
    ) : (
      getCategoryIcon(file.category)
    ),
    name: (
      <div>
        <div style={{ fontWeight: 500 }}>{file.file_name}</div>
        {file.description && (
          <div
            style={{
              fontSize: 'var(--cds-productive-heading-01-font-size)',
              color: 'var(--cds-text-secondary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '20rem',
            }}
          >
            {file.description}
          </div>
        )}
      </div>
    ),
    category: <Tag type={file.category === 'portfolio' ? 'blue' : 'gray'}>{file.category}</Tag>,
    size: formatFileSize(file.file_size),
    visibility: (
      <Tag type={file.visibility === 'public' ? 'green' : file.visibility === 'unlisted' ? 'purple' : 'gray'}>
        {file.visibility}
      </Tag>
    ),
    created: formatDate(file.created_at),
    stats: (
      <div style={{ fontSize: 'var(--cds-body-compact-01-font-size)' }}>
        <div>{file.view_count} views</div>
        <div>{file.download_count} downloads</div>
      </div>
    ),
    actions: (
      <OverflowMenu size="sm" flipped>
        <OverflowMenuItem itemText="View" onClick={() => onFileSelect && onFileSelect(file)} />
        <OverflowMenuItem itemText="Download" onClick={() => handleFileDownload(file)} />
        <OverflowMenuItem itemText="Share" onClick={() => handleShare(file)} />
        <OverflowMenuItem itemText="Delete" onClick={() => onFileDelete && onFileDelete(file.file_id)} isDelete />
      </OverflowMenu>
    ),
  }));

  if (loading) {
    return (
      <div className="p-4">
        <SkeletonText paragraph lineCount={5} />
      </div>
    );
  }

  if (error) {
    return <InlineNotification kind="error" title="Error loading gallery" subtitle={error} />;
  }

  return (
    <div className="media-gallery">
      {/* Category Tabs */}
      <Tabs selectedIndex={['all', 'images', 'videos', 'documents', 'portfolios'].indexOf(activeCategory)}>
        <TabList aria-label="Gallery categories">
          <Tab onClick={() => setActiveCategory('all')}>All ({gallery.all.length})</Tab>
          <Tab onClick={() => setActiveCategory('images')}>Images ({gallery.images.length})</Tab>
          <Tab onClick={() => setActiveCategory('videos')}>Videos ({gallery.videos.length})</Tab>
          <Tab onClick={() => setActiveCategory('documents')}>Documents ({gallery.documents.length})</Tab>
          <Tab onClick={() => setActiveCategory('portfolios')}>Portfolio ({gallery.portfolios.length})</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            {/* View Mode Toggle */}
            <div style={{ marginBottom: 'var(--cds-spacing-05)', display: 'flex', justifyContent: 'flex-end' }}>
              <ContentSwitcher
                onChange={(e: any) => setViewMode(e.name as 'grid' | 'list')}
                selectedIndex={viewMode === 'list' ? 0 : 1}
              >
                <Switch name="list" text="List" />
                <Switch name="grid" text="Grid" />
              </ContentSwitcher>
            </div>

            {viewMode === 'list' ? (
              <DataTable rows={rows} headers={headers} isSortable useZebraStyles size="lg">
                {({
                  rows,
                  headers,
                  getTableProps,
                  getHeaderProps,
                  getRowProps,
                  getSelectionProps,
                  getTableContainerProps,
                  selectedRows,
                }: any) => (
                  <TableContainer {...getTableContainerProps()}>
                    <TableToolbar>
                      <TableBatchActions
                        shouldShowBatchActions={selectedRows.length > 0}
                        totalSelected={selectedRows.length}
                        onCancel={() => setSelectedFiles([])}
                      >
                        <TableBatchAction
                          renderIcon={Download}
                          onClick={() => {
                            /* Implement bulk download */
                          }}
                        >
                          Download
                        </TableBatchAction>
                        <TableBatchAction renderIcon={Delete} onClick={handleBulkDelete} hasDivider>
                          Delete
                        </TableBatchAction>
                      </TableBatchActions>
                      <TableToolbarContent>
                        <TableToolbarSearch
                          onChange={(e: any) => setSearchTerm(e.target.value)}
                          placeholder="Search files..."
                        />
                        <TableToolbarMenu renderIcon={Filter}>
                          <TableToolbarAction onClick={() => {}}>Filter by date</TableToolbarAction>
                          <TableToolbarAction onClick={() => {}}>Filter by size</TableToolbarAction>
                        </TableToolbarMenu>
                        <Button
                          size="sm"
                          renderIcon={CloudUpload}
                          onClick={() => {
                            /* Navigate to upload */
                          }}
                        >
                          Upload Files
                        </Button>
                      </TableToolbarContent>
                    </TableToolbar>
                    <Table {...getTableProps()} aria-label="Media files">
                      <TableHead>
                        <TableRow>
                          {enableBulkOperations && <TableSelectAll {...getSelectionProps()} />}
                          {headers.map((header: any) => (
                            <TableHeader {...getHeaderProps({ header })} key={header.key}>
                              {header.header}
                            </TableHeader>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map((row: any) => (
                          <TableRow {...getRowProps({ row })} key={row.id}>
                            {enableBulkOperations && <TableSelectRow {...getSelectionProps({ row })} />}
                            {row.cells.map((cell: any) => (
                              <TableCell key={cell.id}>{cell.value}</TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </DataTable>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: 'var(--cds-spacing-05)',
                }}
              >
                {files.map(file => (
                  <Tile key={file.file_id} style={{ cursor: 'pointer' }}>
                    <div onClick={() => onFileSelect && onFileSelect(file)}>
                      {file.thumbnail_url ? (
                        <img
                          src={file.thumbnail_url}
                          alt={file.file_name}
                          style={{
                            width: '100%',
                            height: '8rem',
                            objectFit: 'cover',
                            borderRadius: 'var(--cds-border-radius)',
                            marginBottom: 'var(--cds-spacing-02)',
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: '100%',
                            height: '8rem',
                            backgroundColor: 'var(--cds-layer-01)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 'var(--cds-border-radius)',
                            marginBottom: 'var(--cds-spacing-02)',
                          }}
                        >
                          {getCategoryIcon(file.category)}
                        </div>
                      )}
                      <div style={{ padding: '0 var(--cds-spacing-02) var(--cds-spacing-02)' }}>
                        <div
                          style={{
                            fontWeight: 500,
                            fontSize: 'var(--cds-body-compact-01-font-size)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {file.file_name}
                        </div>
                        <div
                          style={{
                            fontSize: 'var(--cds-helper-text-01-font-size)',
                            color: 'var(--cds-text-secondary)',
                          }}
                        >
                          {formatFileSize(file.file_size)}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            gap: 'var(--cds-spacing-01)',
                            marginTop: 'var(--cds-spacing-01)',
                          }}
                        >
                          <Tag size="sm" type="gray">
                            {file.category}
                          </Tag>
                          <Tag size="sm" type={file.visibility === 'public' ? 'green' : 'gray'}>
                            {file.visibility}
                          </Tag>
                        </div>
                      </div>
                    </div>
                  </Tile>
                ))}
              </div>
            )}

            {/* Pagination */}
            <Pagination
              totalItems={totalItems}
              pageSize={pageSize}
              pageSizes={[10, 20, 30, 40, 50]}
              page={page}
              onChange={({ page, pageSize }) => {
                setPage(page);
                setPageSize(pageSize);
              }}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Share Modal */}
      <Modal
        open={shareModalOpen}
        onRequestClose={() => setShareModalOpen(false)}
        modalHeading="Share File"
        primaryButtonText="Generate Link"
        secondaryButtonText="Cancel"
      >
        {selectedFileForShare && (
          <div>
            <p>Generate a sharing link for: {selectedFileForShare.file_name}</p>
            {/* Add share options here */}
          </div>
        )}
      </Modal>
    </div>
  );
};
